/**
 * 视频区布局：宫格 / 演讲者视图、置顶、隐藏无视频成员。
 *
 * 单独放一个 store 而不是组件内 state：切到白板页再切回来，
 * 视频区会重新挂载，老师刚置顶的那个学生不能因此被忘掉。
 */

import { create } from 'zustand'

import { USER_NONE } from '../lib/protocol'
import { isMobileLayout } from '../lib/mobile'

export type StageLayout = 'grid' | 'speaker'

/**
 * 置顶「我自己」用的特殊席位值。席位号都 ≥ 0，USER_ALL=-1、USER_NONE=-2 已被协议占用，
 * 曾经写成 -2 与 USER_NONE 撞值：取消置顶的瞬间「自己」就被当成置顶了。
 */
export const PIN_SELF = -3

interface LayoutState {
  layout: StageLayout
  /** 置顶席位；USER_NONE 未置顶，PIN_SELF 置顶自己 */
  pinnedId: number
  setLayout: (l: StageLayout) => void
  /** 再点一次同一个人即取消置顶 */
  togglePin: (id: number) => void
  clearPin: () => void
}

export const useLayoutStore = create<LayoutState>((set) => ({
  // 手机默认演讲者视图：屏幕只有巴掌大，宫格里每个人都是拇指大小的一格，
  // 而学员来是看老师的。桌面保持宫格 —— 那里有地方摆得下所有人。
  layout: isMobileLayout() ? 'speaker' : 'grid',
  pinnedId: USER_NONE,
  setLayout: (layout) => set({ layout }),
  // 置顶隐含「看大图」，顺手切到演讲者视图；宫格里置顶没有视觉意义。
  togglePin: (id) =>
    set((s) => (s.pinnedId === id ? { pinnedId: USER_NONE } : { pinnedId: id, layout: 'speaker' })),
  clearPin: () => set({ pinnedId: USER_NONE }),
}))

/**
 * 决定演讲者视图里谁上大图。纯函数，便于单测。
 * 优先级：置顶 > 正在共享屏幕的人（由调用方先处理）> 说话声最大的有视频者 > 第一个有视频者。
 *
 * 「我自己」不参与：腾讯会议里自己永远待在缩略条，自己说话也不会把大图换成自己 ——
 * 学员来是看老师的，看着自己的镜像没有意义。只有房里只剩自己、或主动置顶自己时才例外。
 */
export function pickSpeaker(
  candidates: number[],
  pinnedId: number,
  speaking: Map<number, number>,
): number {
  if (candidates.length === 0) return USER_NONE
  if (pinnedId !== USER_NONE && candidates.includes(pinnedId)) return pinnedId
  const others = candidates.filter((id) => id !== PIN_SELF)
  const pool = others.length > 0 ? others : candidates
  let best = pool[0]
  let bestLevel = -1
  for (const id of pool) {
    const lv = speaking.get(id) ?? 0
    if (lv > bestLevel && lv > 15) {
      best = id
      bestLevel = lv
    }
  }
  return best
}
